'use client';

import React from 'react';
import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { ErrorMessage } from './fieldset';

export type MultiComboboxOption<T> = {
  value: T;
  label: string;
  description?: string;
  /**
   * Extra text the filter matches but the row never shows. For an option that
   * stands in for a set of things — one row representing a form published in
   * fifty states — the names of those things belong in the search index without
   * turning the row into a wall of text.
   */
  keywords?: string;
};

/**
 * A searchable multi-select combobox.
 *
 * Visually matches the single-select `Combobox` (same outer shell, focus
 * ring, and dropdown styling). Selected items are rendered as removable
 * `bg-primary-100` chips inside the trigger — the same chip style used by
 * `MultiListbox` — so the two controls share a consistent selection
 * language across the product.
 *
 * The inline text input filters the dropdown list as the user types, with
 * matched substrings highlighted in yellow. Selections never fill the input
 * field; instead they appear immediately as chips and the input is cleared
 * so the user can keep searching.
 */
export function MultiCombobox<T extends { toString(): string }>(props: {
  value?: T[];
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  options: MultiComboboxOption<T>[];
  /**
   * When set, options beyond this count become un-selectable once the limit
   * is reached. Already-selected options remain interactive (i.e. can be
   * deselected).
   */
  maxSelections?: number;
  onChange: (values: T[]) => void;
}) {
  // queryRef is updated synchronously so that HeadlessUI's displayValue
  // callback (which fires after a selection to reset the input) always sees
  // the freshly-cleared value, avoiding a one-render flicker.
  const queryRef = React.useRef('');
  const [query, setQueryState] = React.useState('');

  const setQuery = React.useCallback((q: string) => {
    queryRef.current = q;
    setQueryState(q);
  }, []);

  const selectedOptions = React.useMemo(
    () =>
      props.options.filter((opt) =>
        (props.value ?? []).some((v) => v.toString() === opt.value.toString())
      ),
    [props.options, props.value]
  );

  // Every whitespace-separated term must appear somewhere in the option, rather
  // than the whole query having to be one contiguous substring. Long option
  // labels are usually "identifier (description)" pairs, so a user who types
  // the two halves they remember — "137 montana" for "ACORD 137 (Commercial
  // Auto, Montana)" — would otherwise get no results at all.
  const queryTerms = React.useMemo(() => query.toLowerCase().split(/\s+/).filter(Boolean), [query]);

  const filteredOptions = React.useMemo(() => {
    if (queryTerms.length === 0) return props.options;
    return props.options.filter((opt) => {
      const haystack = [opt.label, opt.description, opt.keywords, opt.value.toString()]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return queryTerms.every((term) => haystack.includes(term));
    });
  }, [props.options, queryTerms]);

  // Longest term first so that when one term is a prefix of another only the
  // longer match is highlighted.
  const queryRegex = React.useMemo(() => {
    if (queryTerms.length === 0) return null;
    const alternation = [...queryTerms]
      .sort((a, b) => b.length - a.length)
      .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|');
    return new RegExp(`(${alternation})`, 'gi');
  }, [queryTerms]);

  const atLimit = props.maxSelections != null && selectedOptions.length >= props.maxSelections;

  const handleChange = (newSelected: MultiComboboxOption<T>[]) => {
    setQuery('');
    props.onChange(newSelected.map((o) => o.value));
  };

  const removeValue = (val: T) => {
    props.onChange((props.value ?? []).filter((v) => v.toString() !== val.toString()));
  };

  const highlightMatch = (text: string) => {
    if (!queryRegex) return <span>{text}</span>;
    return (
      <>
        {text.split(queryRegex).map((part, i) =>
          queryTerms.includes(part.toLowerCase()) ? (
            <span
              key={i}
              // dark-mode-exempt: the match chip keeps its yellow in both themes;
              // the ink on it flips instead.
              className="bg-yellow-300 text-neutral-600 font-semibold dark:text-neutral-900"
            >
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  return (
    <>
      <HeadlessCombobox
        as="div"
        data-slot="control"
        value={selectedOptions}
        onChange={handleChange}
        multiple
        disabled={props.disabled}
        data-invalid={props.error ? true : undefined}
        immediate
      >
        <span
          className={clsx([
            // Basic layout — mirrors Combobox outer shell exactly
            'relative block w-full',
            'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
            'dark:before:hidden',
            'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:focus-within:ring-2 after:focus-within:ring-primary-500',
            // Key off the input, not any descendant. At maxSelections,
            // unselected options get data-disabled; :has([data-disabled])
            // would fade this whole span — including the in-DOM dropdown,
            // whose white background then shows the page through it.
            'has-[input[data-disabled]]:opacity-50 before:has-[input[data-disabled]]:bg-slate-950/5 before:has-[input[data-disabled]]:shadow-none',
            'before:has-[[data-invalid]]:shadow-red-500/10'
          ])}
          data-invalid={props.error ? true : undefined}
        >
          {/*
           * Chips + inline search input.
           *
           * The wrapper div carries the border and background so the chips
           * and the ComboboxInput sit inside the same visual "field". The
           * ComboboxButton (chevron) is absolutely positioned on the right.
           */}
          <div
            className={clsx([
              'relative flex flex-wrap items-center gap-x-1.5 gap-y-1 w-full appearance-none rounded-lg',
              'py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
              'min-h-11 sm:min-h-9',
              'pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing[9])-1px)] sm:pl-[calc(theme(spacing[3])-1px)]',
              'border border-slate-950/10 dark:border-white/10',
              'bg-transparent dark:bg-white/5',
              props.error && 'border-red-500'
            ])}
          >
            {/* Selected-value chips — same visual style as MultiListbox selected options */}
            {selectedOptions.map((opt) => (
              <span
                key={opt.value.toString()}
                className="inline-flex items-center gap-1 self-center shrink-0 bg-primary-100 dark:bg-primary-500/20 rounded px-2 text-xs py-0.5"
              >
                {opt.label}
                <button
                  type="button"
                  aria-label={`Remove ${opt.label}`}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 focus:outline-none"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeValue(opt.value);
                  }}
                >
                  <XMarkIcon className="size-3" />
                </button>
              </span>
            ))}

            {/*
             * ComboboxInput is the search field. displayValue always returns
             * queryRef.current so that after a selection HeadlessUI resets
             * the input to '' rather than filling it with a selected label.
             */}
            <ComboboxInput
              className={clsx([
                'flex-1 min-w-[8rem] self-center bg-transparent border-0 p-0',
                'text-base/6 text-slate-950 placeholder:text-slate-500 dark:placeholder:text-slate-400 sm:text-sm/6 dark:text-white',
                'focus:outline-none focus:ring-0',
                'dark:[color-scheme:dark]'
              ])}
              displayValue={() => queryRef.current}
              onChange={(event) => setQuery(event.target.value)}
              onBlur={() => setQuery('')}
              placeholder={selectedOptions.length === 0 ? props.placeholder : undefined}
              data-invalid={props.error ? true : undefined}
            />
          </div>

          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon
              className="h-5 w-5 text-neutral-400 dark:text-neutral-500"
              aria-hidden="true"
            />
          </ComboboxButton>

          {filteredOptions.length > 0 && (
            <ComboboxOptions
              /*
               * No `anchor` prop — renders in the DOM tree rather than in a
               * portal. This lets us use `left-0 right-0` (relative to the
               * outer `span` which has `position:relative`) so the dropdown
               * is always the exact same width as the input field and always
               * opens from its left edge, regardless of how many chips are
               * shown. The Dialog component does not use `overflow:hidden` on
               * its panel, so in-DOM rendering is not clipped.
               */
              className="absolute left-0 right-0 top-full mt-1 z-[200] max-h-64 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-neutral-800 dark:ring-white/10 data-[closed]:hidden"
            >
              {filteredOptions.map((option) => {
                const isSelected = selectedOptions.some(
                  (s) => s.value.toString() === option.value.toString()
                );
                const isDisabled = atLimit && !isSelected;
                return (
                  <ComboboxOption
                    key={option.value.toString()}
                    value={option}
                    disabled={isDisabled}
                    /*
                     * Highlighting splits the label across a span per matched
                     * term, and the accessible name computed from those spans
                     * comes out word-separated ("ACORD 130 ( Workers ' Comp
                     * ensation)"). Naming the row explicitly keeps what a
                     * screen reader announces identical to what it says with an
                     * empty search box.
                     */
                    aria-label={[option.label, option.description].filter(Boolean).join(' — ')}
                    className={clsx(
                      'group relative cursor-default select-none py-2 pl-3 pr-9',
                      'text-neutral-900 dark:text-white',
                      'data-[focus]:bg-primary-500 data-[focus]:text-white',
                      'data-[disabled]:opacity-40'
                    )}
                  >
                    <div>
                      <span className={clsx('block truncate', isSelected && 'font-semibold')}>
                        {highlightMatch(option.label)}
                      </span>
                      {option.description && (
                        <span className="block text-xs text-neutral-500 group-data-[focus]:text-neutral-100 dark:text-neutral-400">
                          {highlightMatch(option.description)}
                        </span>
                      )}
                    </div>

                    {isSelected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 dark:text-primary-400 group-data-[focus]:text-white">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </ComboboxOption>
                );
              })}
            </ComboboxOptions>
          )}
        </span>
      </HeadlessCombobox>
      {!!props.error && <ErrorMessage>{props.error}</ErrorMessage>}
    </>
  );
}
